<template>
  <div class="snackbar-container">
    <TransitionGroup name="snackbar-list" tag="div" class="snackbar-wrapper">
      <div
        v-for="snackbar in snackbars"
        :key="snackbar.id"
        class="snackbar"
        :class="[`snackbar-${snackbar.type}`]"
      >
        <div class="snackbar-icon">
          <!-- Error Icon -->
          <svg
            v-if="snackbar.type === 'error'"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>

          <!-- Success Icon -->
          <svg
            v-else-if="snackbar.type === 'success'"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>

          <!-- Warning Icon -->
          <svg
            v-else-if="snackbar.type === 'warning'"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            ></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>

          <!-- Info Icon -->
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </div>

        <div class="snackbar-content">
          <h4 v-if="snackbar.title" class="snackbar-title">
            {{ snackbar.title }}
          </h4>
          <p class="snackbar-message">{{ snackbar.message }}</p>
        </div>

        <button
          class="snackbar-close"
          @click="removeSnackbar(snackbar.id)"
          aria-label="Cerrar notificación"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { useSnackbar } from "../composables/useSnackbar";
const { snackbars, removeSnackbar } = useSnackbar();
</script>

<style scoped>
.snackbar-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  pointer-events: none; /* Permite clickar debajo del contenedor */
}

.snackbar-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-end;
}

.snackbar {
  pointer-events: auto; /* Restaura el hover y click en el toast individual */
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  width: 380px;
  max-width: calc(100vw - 3rem);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background-color: var(--color-surface);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.05);
  border-left: 4px solid transparent;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  backdrop-filter: blur(8px);
}

/* Modificadores por Tipo */
.snackbar-error {
  border-left-color: var(--color-danger);
}
.snackbar-error .snackbar-icon {
  color: var(--color-danger);
}

.snackbar-success {
  border-left-color: var(
    --color-primary
  ); /* Podría ser verde si tienes var(--color-success) */
  border-left-color: #10b981;
}
.snackbar-success .snackbar-icon {
  color: #10b981;
}

.snackbar-warning {
  border-left-color: #f59e0b;
}
.snackbar-warning .snackbar-icon {
  color: #f59e0b;
}

.snackbar-info {
  border-left-color: #3b82f6;
}
.snackbar-info .snackbar-icon {
  color: #3b82f6;
}

.snackbar-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.snackbar-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.snackbar-title {
  margin: 0;
  font-weight: 600;
  font-size: 0.95rem;
}

.snackbar-message {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.snackbar-close {
  flex-shrink: 0;
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.25rem;
  margin: -0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.snackbar-close:hover {
  background-color: var(--color-border);
  color: var(--color-text-primary);
}

/* Animaciones del TransitionGroup de Vue */
.snackbar-list-enter-active,
.snackbar-list-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.snackbar-list-enter-from {
  opacity: 0;
  transform: translateX(50px) scale(0.95);
}

.snackbar-list-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>
